import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { processMentions } from './MentionHighlight';

interface MessageContentProps {
  content: string;
  format?: 'text' | 'markdown' | 'html';
  /** Map of userId → displayName for resolving @mentions */
  senderNames?: Record<string, string>;
  /** Current user ID (to highlight self-mentions differently) */
  currentUserId?: string;
  /** Custom emoji map for resolving :shortcodes: (name → image URL) */
  customEmojiMap?: Map<string, string>;
}

/** Replace :name: shortcodes with <img> tags for custom emoji */
function processCustomEmoji(html: string, emojiMap?: Map<string, string>): string {
  if (!emojiMap || emojiMap.size === 0) return html;
  // Match :word: patterns not inside HTML tags
  return html.replace(/:([a-zA-Z0-9_]{2,32}):/g, (match, name) => {
    const url = emojiMap.get(name);
    if (!url) return match;
    return `<img src="${url}" alt=":${name}:" title=":${name}:" class="inline-emoji" style="height:1.2em;width:1.2em;vertical-align:text-bottom;display:inline;" />`;
  });
}

/**
 * Renders message content with rich text support.
 * - format='text': plain text with whitespace preservation
 * - format='markdown' or 'html': sanitized HTML rendering (from Tiptap)
 */
export function MessageContent({ content, format = 'text', senderNames = {}, currentUserId, customEmojiMap }: MessageContentProps) {
  const rendered = useMemo(() => {
    if (format === 'text' || !format) {
      // Plain text — render with link detection, then process raw @uuid mentions
      const linked = linkifyText(content);
      const withMentions = processMentions(linked, senderNames, currentUserId);
      return processCustomEmoji(withMentions, customEmojiMap);
    }

    // HTML content from Tiptap — sanitize and render
    const clean = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'del', 's', 'code', 'pre',
        'blockquote', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3',
        'span',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'data-type', 'data-id', 'data-label', 'data-user-id'],
    });
    // Process mention spans into styled pills, then custom emoji
    const withMentions = processMentions(clean, senderNames, currentUserId);
    return processCustomEmoji(withMentions, customEmojiMap);
  }, [content, format, senderNames, currentUserId, customEmojiMap]);

  if (format === 'text' || !format) {
    return (
      <p
        className="text-sm text-nord-4 mt-0.5 break-words whitespace-pre-wrap leading-relaxed"
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    );
  }

  return (
    <div
      className="text-sm text-nord-4 mt-0.5 break-words leading-relaxed message-content"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

/** Convert URLs in plain text to clickable links */
function linkifyText(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-frost-2 hover:underline">$1</a>',
  );
}
